import { useEffect, useRef } from "react";
import { useLatestCallback } from "@shared/hooks/useLatestCallback";
import { useUrlParam } from "@shared/hooks/useUrlParam";

/** Опции {@link useUrlParamTrigger}. */
export interface UseUrlParamTriggerOptions {
    /**
     * Когда `false`, параметр не потребляется (остаётся в URL).
     * @default true
     */
    enabled?: boolean;
}

/**
 * Одноразовый trigger по query-параметру: появился → `onTrigger(value)` → параметр удалён (`replace`).
 *
 * @param paramKey — имя query-параметра
 * @param onTrigger — обработчик; вызывается один раз на появление значения
 * @param options — {@link UseUrlParamTriggerOptions}
 *
 * @example
 * useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => openFilterWindow());
 *
 * @example
 * // deep-link: дождаться данных карточки, затем открыть overlay
 * useUrlParamTrigger(OPEN_LEAVE_CARD_QUERY_PARAM, (leaveCode) => openLeave(leaveCode), {
 *   enabled: limitCard != null,
 * });
 */
export function useUrlParamTrigger(
    paramKey: string,
    onTrigger: (value: string) => void,
    options: UseUrlParamTriggerOptions = {},
): void {
    const { enabled = true } = options;
    const param = useUrlParam(paramKey);
    const stableOnTrigger = useLatestCallback(onTrigger);
    const triggeredRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (param.value != null) {
            if (!triggeredRef.current) {
                triggeredRef.current = true;
                param.removeValue({ replace: true });
                stableOnTrigger(param.value);
            }
        } else {
            triggeredRef.current = false;
        }
    }, [enabled, param.value, param.removeValue, stableOnTrigger]);
}
